import { useEffect } from 'react';
import { Page } from 'lola-framework-ui-test';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { ControlDock } from './gallery/ControlDock';
import { useGalleryStore } from './gallery/galleryStore';
import {
  DEFAULT_SCREEN_ROUTE,
  galleryScreens,
} from './gallery/screens';
import {
  APP_ROUTES,
  FUND_REQUEST_ROUTES,
  PAYOUT_CLAIM_ROUTES,
  SEND_ROUTES,
  REQUEST_ROUTES,
  DEFERRED_ORIGINATION_ROUTES,
  M2M_ROUTES,
  galleryFlowScreenClass,
} from './constants';
import { CashDepositPage as FundRequestCashDepositRedirect } from './pages/fund-request/CashDepositPage';
import { CashDepositPage as PayoutClaimCashDepositRedirect } from './pages/payout-claim/CashDepositPage';
import { CashDepositPage as SendCashDepositRedirect } from './pages/send/CashDepositPage';
import { ReceiptSummaryPage as FundRequestReceiptSummaryPage } from './pages/fund-request/ReceiptSummaryPage';
import { ReceiptSummaryPage as PayoutClaimReceiptSummaryPage } from './pages/payout-claim/ReceiptSummaryPage';
import { ReceiptSummaryPage as RequestReceiptSummaryPage } from './pages/request/ReceiptSummaryPage';
import { ReceiptSummaryPage as SendReceiptSummaryPage } from './pages/send/ReceiptSummaryPage';
import { ReceiptSummaryPage as DeferredOriginationReceiptSummaryPage } from './pages/deferred-origination/ReceiptSummaryPage';
import {
  useApplyTheme,
  useGalleryPayoutEdit,
  useGalleryTerms,
  useSeedMockData,
} from './gallery/useGallery';
import './gallery/gallery.css';
import './gallery/request.css';
import './gallery/send.css';
import './gallery/payout-claim.css';
import './gallery/deferred-origination.css';
import './gallery/smart.css';
import './gallery/consumer-auth.css';
import './gallery/fund-request.css';
import './gallery/notifications.css';
import './gallery/cash-deposit.css';
import './gallery/m2m.css';
import './gallery/m2m/kapital.styles.css';
import './gallery/m2m/flavors.css';
import './gallery/m2m/landingpage.style.css';
import './gallery/m2m/transactionspage.style.css';
import './gallery/m2m/transfer.flavorb.style.css';
import './gallery/m2m/transfer.flavorc.style.css';
import './gallery/m2m/cardpage.style.css';
import './styles/flow-hero.css';
import './gallery/lola.css';
import './gallery/first-send.css';
import './gallery/phone-capture.css';
import './gallery/sms-terms.css';
import './gallery/icons.css';
import { IconCatalogPage } from './pages/icons/IconCatalogPage';
import { ICON_CATALOG_ROUTE } from './icons/catalog';

const App = () => {
  const font = useApplyTheme();
  useSeedMockData();
  useGalleryTerms();
  useGalleryPayoutEdit();
  const location = useLocation();
  const exporting = useGalleryStore((s) => s.exporting);

  useEffect(() => {
    document.documentElement.classList.toggle('gallery-exporting', exporting);
    return () => {
      document.documentElement.classList.remove('gallery-exporting');
    };
  }, [exporting]);

  return (
    <Page font={{ cdn: font.cdn, name: font.name }}>
      <div className={`gallery-app ${exporting ? 'is-exporting' : ''}`}>
        {/* `key` forces a remount on every route change so the entry
            transition replays for each screen. */}
        <div
          className={`gallery-screen ${exporting ? 'gallery-screen--capture' : ''} ${galleryFlowScreenClass(location.pathname)}`}
          key={`${location.pathname}${location.search}`}
        >
          <Routes location={location}>
            {galleryScreens.map(({ id, route, Component }) => (
              <Route key={id} path={route.split('?')[0]} element={<Component />} />
            ))}
            <Route
              path={FUND_REQUEST_ROUTES.CASH_DEPOSIT}
              element={<FundRequestCashDepositRedirect />}
            />
            <Route
              path={PAYOUT_CLAIM_ROUTES.CASH_DEPOSIT}
              element={<PayoutClaimCashDepositRedirect />}
            />
            <Route
              path={SEND_ROUTES.CASH_DEPOSIT}
              element={<SendCashDepositRedirect />}
            />
            <Route
              path={`${FUND_REQUEST_ROUTES.RECEIPT}/:operationId`}
              element={<FundRequestReceiptSummaryPage />}
            />
            <Route
              path={`${PAYOUT_CLAIM_ROUTES.RECEIPT}/:operationId`}
              element={<PayoutClaimReceiptSummaryPage />}
            />
            <Route
              path={`${REQUEST_ROUTES.RECEIPT}/:requestId`}
              element={<RequestReceiptSummaryPage />}
            />
            <Route
              path={`${SEND_ROUTES.RECEIPT}/:operationId`}
              element={<SendReceiptSummaryPage />}
            />
            <Route
              path={`${DEFERRED_ORIGINATION_ROUTES.RECEIPT}/:originationId`}
              element={<DeferredOriginationReceiptSummaryPage />}
            />
            <Route
              path={APP_ROUTES.LEGAL_PAGE}
              element={<Navigate to={M2M_ROUTES.LEGAL} replace />}
            />
            <Route path={ICON_CATALOG_ROUTE} element={<IconCatalogPage />} />
            <Route
              path="*"
              element={<Navigate to={DEFAULT_SCREEN_ROUTE} replace />}
            />
          </Routes>
        </div>
        <ControlDock />
      </div>
    </Page>
  );
};

export default App;
